import { Component, Element, Event, EventEmitter, Fragment, h, Host, Prop, State, Watch } from "@stencil/core";

import {
  StzhDatalistBigLabelChangeEvent,
  StzhDatalistDirection,
  StzhDatalistDirectionChangeEvent,
  StzhDatalistItemActionClickEvent,
  StzhDatalistItemClickEvent,
  StzhDatalistVariant,
  StzhDatalistVariantChangeEvent,
  StzhStatusType,
} from "../../index";

import { addWordBreakToLinks } from "../../utils/utils";
import { StzhLocaleComponent } from "../../index";

let datalistItemCounter = 0;

/**
 * @slot - Slot for nested datalist
 * @slot value - Slot for value (or use value slot instead)
 * @slot readspeaker - Slot for readspeaker
 */
@Component({
  tag: "stzh-datalist-item",
  styleUrl: "stzh-datalist-item.scss",
  scoped: true,
})
export class StzhDatalistItem {
  /** Translation strings */
  @Prop() localization: StzhLocaleComponent;

  /** Link */
  @Prop({ reflect: true }) href: string;

  /** Rel (if href is used) */
  @Prop() rel: string;

  /** Target (if href is used) */
  @Prop() target: string;

  /** Download attribute of link (if `href` used) */
  @Prop() download: string;

  /** Leading icon */
  @Prop() leadingIcon: string;

  /** Icon of action button (if href is used, will only be for presentation) */
  @Prop() icon: string;

  /** Icon position */
  @Prop() iconPosition: "left" | "right" = "right";

  /** Overwrite aria-label of action button (default aria label is `label` and `value`). */
  @Prop() iconLabel: string;

  /** Tooltip content of action button. Overwrites aria-label (set by default or `iconLabel`) of action button. */
  @Prop() iconTooltip: string;

  /** Href of action button */
  @Prop() iconHref: string;

  /** Label */
  @Prop() label: string = "";

  /** Value (or use value slot instead) */
  @Prop() value: string = "";

  /** Whether value should be truncate or not */
  @Prop() valueTruncate: boolean = false;  

  /** Readspeaker label */
  @Prop() readspeakerLabel: string = "";

  /** Readspeaker link */
  @Prop() readspeakerHref: string = "";

  /** Meta */
  @Prop({ reflect: true }) meta: string | string[] = "";
  private _meta: string[] = [];

  /** Status Label */
  @Prop({ reflect: true }) statusLabel: string = "";

  /** Status Type */
  @Prop() statusType: StzhStatusType = "default";

  /** Aria expanded of link/button */
  @Prop({ reflect: true, attribute: "a11y-expanded" }) a11yExpanded: boolean;

  /** Aria controls of link/button */
  @Prop({ attribute: "a11y-controls" }) a11yControls: string;

  /** Item action click event */
  @Event() stzhItemActionClick: EventEmitter<StzhDatalistItemActionClickEvent>;

  /** Item click event */
  @Event() stzhClick: EventEmitter<StzhDatalistItemClickEvent>;

  @Element() element: HTMLStzhDatalistItemElement;

  @Watch("meta")
  metaWatcher(newValue: string[] | string) {
    if (typeof newValue === "string") {
      try {
        this._meta = JSON.parse(newValue);
      } catch (ex) {
        this._meta = newValue === "" ? [] : [newValue];
      }
    } else {
      this._meta = newValue;
    }
  }

  @Watch("rel")
  externalWatcher() {
    this.external = this.rel && this.rel.includes("external");
  }

  @Watch("href")
  hrefWatcher() {
    this.isPhone = this.href && (this.href.startsWith("tel:") || this.href.startsWith("fax:"));
  }

  @State() direction: StzhDatalistDirection = "vertical";
  @State() variant: StzhDatalistVariant = "default";
  @State() bigLabel: boolean = false;

  private external: boolean = false;
  private isPhone: boolean = false;
  private datalist: HTMLStzhDatalistElement;
  private datalistItemId: string;

  private onActionClick = (originalEvent: MouseEvent) => {
    this.stzhItemActionClick.emit({
      component: "stzh-datalist-item",
      originalEvent,
    });
  };

  private onClick = (event: MouseEvent) => {
    this.stzhClick.emit({
      component: "stzh-datalist-item",
      originalEvent: event,
      href: this.href,
    });
  };

  private handleDirectionChange = (event: CustomEvent<StzhDatalistDirectionChangeEvent>) => {
    this.direction = event.detail.value;
  };

  private handleVariantChange = (event: CustomEvent<StzhDatalistVariantChangeEvent>) => {
    this.variant = event.detail.value;
  };

  private handleBigLabelChange = (event: CustomEvent<StzhDatalistBigLabelChangeEvent>) => {
    this.bigLabel = event.detail.value;
  };

  private addWordBreakToLinks() {
    const links = Array.from(
      this.element?.getElementsByClassName("stzh-datalist-item__value-text") || []
    );
    addWordBreakToLinks(links as HTMLElement[]);
  }

  private renderInner(isButton: boolean, hasActionButton: boolean): DocumentFragment {
    const Item = isButton ? "button" : this.href ? "a" : "div";

    const Icon = () => (
      <Fragment>
        {(this.href || this.icon) && !hasActionButton && !isButton && (
          <div class="stzh-datalist-item__label-icon">
            <stzh-icon
              class="stzh-datalist-item__icon"
              name={this.icon ? this.icon : this.isPhone ? "phone" : this.external && "external-link"}
            ></stzh-icon>
          </div>
        )}

        {isButton && (
          <div class="stzh-datalist-item__label-icon">
            <stzh-icon class="stzh-datalist-item__icon is-closed" name={this.icon ? this.icon : "plus"}></stzh-icon>
            <stzh-icon class="stzh-datalist-item__icon is-open" name={this.icon ? this.icon : "minus"}></stzh-icon>
          </div>
        )}
      </Fragment>
    );

    return (
      <Fragment>
        {this.variant === "table" && (
          <Item
            aria-hidden={this.label ? null : "true"}
            tabindex="-1"
            id={`${this.datalistItemId}-label`}
            class={{
              "stzh-datalist-item__label": true,
              "is-button": isButton,
              "is-link": !!this.href,
            }}
            href={this.href}
            rel={this.rel}
            target={this.target}
            download={this.download}
            onClick={this.onClick}
          >
            {this.label}
          </Item>
        )}

        <Item
          id={`${this.datalistItemId}-value`}
          class={{
            "stzh-datalist-item__value": true,
            "is-button": isButton,
            "is-link": !!this.href,
          }}
          href={this.href}
          rel={this.rel}
          target={this.target}
          download={this.download}
          // aria-labelledby={Item !== 'div' ? `${this.datalistItemId}-label ${this.datalistItemId}-value ${this.datalistItemId}-meta` : null}
          aria-labelledby={
            Item !== "div"
              ? `${this.variant === "table" ? `${this.datalistItemId}-label` : ""} ${this.datalistItemId}-value ${
                  this.datalistItemId
                }-meta`
              : null
          }
          aria-controls={this.a11yControls || null}
          aria-expanded={typeof this.a11yExpanded !== "undefined" ? (this.a11yExpanded ? "true" : "false") : null}
          onClick={this.onClick}
        >
          {this.variant === "default" && <div class="stzh-datalist-item__label">{this.label}</div>}

          {this.external && (
            <div class="stzh-datalist-item__vhidden">{this.localization.$globals.externalLinkLabel}</div>
          )}
          {this.download && (
            <div class="stzh-datalist-item__vhidden">{this.localization.$globals.downloadLinkLabel}</div>
          )}

          <div class="stzh-datalist-item__value-wrapper">
            {this.iconPosition === "left" && <Icon></Icon>}
            {this.value ? (
              <stzh-text curve="none" class="stzh-datalist-item__value-text" innerHTML={this.value}></stzh-text>
            ) : (
              <stzh-text curve="none" class="stzh-datalist-item__value-text">
                <slot name="value"></slot>
              </stzh-text>
            )}
            {this.iconPosition === "right" && <Icon></Icon>}
          </div>
        </Item>

        <Item
          aria-hidden={this.statusLabel || this._meta.length > 0 ? null : "true"}
          tabindex="-1"
          id={`${this.datalistItemId}-meta`}
          class={{
            "stzh-datalist-item__meta": true,
            "is-button": isButton,
            "is-link": !!this.href,
          }}
          href={this.href}
          rel={this.rel}
          target={this.target}
          download={this.download}
          onClick={this.onClick}
        >
          {this.statusLabel && (
            <stzh-status
              class="stzh-datalist-item__meta-status"
              label={this.statusLabel}
              type={this.statusType}
            ></stzh-status>
          )}
          {this._meta.length > 0 && (
            <span class="stzh-datalist-item__meta-text">
              {this._meta.map((meta, index) => (
                <Fragment>
                  <span class="stzh-datalist-item__meta-text-inner">{meta}</span>
                  {index + 1 !== this._meta.length && <span class="stzh-datalist-item__meta-separator">|</span>}
                </Fragment>
              ))}
            </span>
          )}
        </Item>

        {this.readspeakerHref && this.readspeakerLabel && (
          <stzh-button
            class="stzh-datalist-item__readspeaker"
            variant="tertiary-plain"
            no-padding-left="true"
            no-padding-right="true"
            icon-position="right"
            icon="volume-medium"
            size="small"
            href={this.readspeakerHref}
            target="_blank"
          >
            {this.readspeakerLabel}
          </stzh-button>
        )}
      </Fragment>
    );
  }

  private renderLeadingIcon(isButton: boolean): HTMLElement {
    const Item = isButton ? "button" : this.href ? "a" : "div";

    return (
      <Item
        role="presentation"
        aria-hidden="true"
        tabindex="-1"
        class={{
          "stzh-datalist-item__leading": true,
          "is-button": isButton,
          "is-link": !!this.href,
        }}
        href={this.href}
        rel={this.rel}
        target={this.target}
        download={this.download}
        onClick={this.onClick}
      >
        {this.leadingIcon && (
          <div class="stzh-datalist-item__leading-icon-container">
            <stzh-icon class="stzh-datalist-item__leading-icon" name={this.leadingIcon}></stzh-icon>
          </div>
        )}
      </Item>
    );
  }

  async componentDidLoad() {
    // execute connectedCallbck again, because sometimes has problem rendering
    this.connectedCallback();
    this.addWordBreakToLinks();
  }

  async componentWillLoad() {
    this.datalistItemId = `stzh-datalist-item-${datalistItemCounter++}`;
    this.metaWatcher(this.meta);
    this.externalWatcher();
    this.hrefWatcher();

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "datalist-item");
    }
  }

  async connectedCallback() {
    this.datalist = this.element.closest("stzh-datalist");

    if (this.datalist) {
      this.variant = this.datalist.variant;
      this.bigLabel = this.datalist.bigLabel;
      this.direction = await this.datalist.getCurrentDirection();

      this.datalist?.addEventListener("stzhDirectionChange", this.handleDirectionChange);
      this.datalist?.addEventListener("stzhVariantChange", this.handleVariantChange);
      this.datalist?.addEventListener("stzhBigLabelChange", this.handleBigLabelChange);
    }
  }

  disconnectedCallback() {
    if (this.datalist) {
      this.datalist?.removeEventListener("stzhDirectionChange", this.handleDirectionChange);
      this.datalist?.removeEventListener("stzhVariantChange", this.handleVariantChange);
      this.datalist?.removeEventListener("stzhBigLabelChange", this.handleBigLabelChange);
      this.datalist = null;
    }
  }

  render() {
    const isButton = typeof this.a11yExpanded !== "undefined" || typeof this.a11yControls !== "undefined";

    const hasActionButton = !!((!this.href || this.iconHref || this.iconTooltip) && this.icon);

    const classes = {
      "stzh-datalist-item": true,
      "stzh-datalist-item--is-interactive": !!this.href || isButton,
      "stzh-datalist-item--is-link": !!this.href,
      "stzh-datalist-item--is-button": isButton,
      "stzh-datalist-item--is-expanded": this.a11yExpanded,
      "stzh-datalist-item--big-label": this.bigLabel,
      [`stzh-datalist-item--direction-${this.direction}`]: !!this.direction && this.variant === "default",
      [`stzh-datalist-item--${this.variant}`]: !!this.variant,
    };

    let ActionItem = isButton ? "button" : hasActionButton ? "div" : "a";

    const ActionButton = () => {
      return (
        <stzh-button
          class={{
            "stzh-datalist-item__action": true,
            "is-button": true,
          }}
          variant="tertiary"
          size="small"
          aria-label={this.iconLabel}
          iconOnly={true}
          href={this.iconHref}
          icon={this.icon}
          onClick={this.onActionClick}
        ></stzh-button>
      );
    };

    return (
      <Host role="listitem" class={{'value-truncate': this.valueTruncate}}>
        <div class={classes}>
          {this.direction === "horizontal" || this.variant === "table" ? (
            <Fragment>
              {this.renderLeadingIcon(isButton)}
              {this.renderInner(isButton, hasActionButton)}
            </Fragment>
          ) : (
            <Fragment>
              {this.renderLeadingIcon(isButton)}
              <div class="stzh-datalist-item__wrapper">{this.renderInner(isButton, hasActionButton)}</div>
            </Fragment>
          )}
          <ActionItem
            role={hasActionButton ? null : "presentation"}
            aria-hidden={hasActionButton ? null : "true"}
            tabindex="-1"
            class={{
              "stzh-datalist-item__action-list": true,
              "is-button": isButton,
              "is-link": !!this.href && !this.iconHref,
            }}
            href={!this.iconHref ? this.href : null}
            rel={!this.iconHref ? this.rel : null}
            target={!this.iconHref ? this.target : null}
            download={!this.iconHref ? this.download : null}
          >
            {hasActionButton &&
              (this.iconTooltip ? (
                <stzh-tooltip content={this.iconTooltip}>{ActionButton()}</stzh-tooltip>
              ) : (
                ActionButton()
              ))}
          </ActionItem>

          <div class="stzh-datalist-item__spacer"></div>
        </div>

        <div class="stzh-datalist-item__nested">
          <div class="stzh-datalist-item__nested-inner">
            <slot></slot>
          </div>
        </div>
      </Host>
    );
  }
}
